import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useBulkActionToast } from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import React, { useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAppRouteParams } from "../../../../../AppRoutes";
import {
  Version,
  useGetVersion,
  useUpdateVersion,
} from "../../../../../domain/Version";
import { DIALOG_CONFIRM_MAX_WIDTH } from "../../../../../styles";
import { DialogPhaseStatesType, useDialog } from "../../../helpers/hooks";
import { ReferredFromState } from "../../helper";

const useTranslation = translation.use(nsVersion);

export const DialogVersionDelete: React.FC<
  {
    versions: Version[];
    selectedVersionIds: string[];
    setSelectedVersionIds: React.Dispatch<React.SetStateAction<string[]>>;
    isIncludingReferencedSheets: ReferredFromState;
  } & DialogPhaseStatesType
> = ({
  versions,
  selectedVersionIds,
  setSelectedVersionIds,
  isIncludingReferencedSheets,
  dialogPhase,
  setDialogPhase,
}) => {
  const { t } = useTranslation();
  const { organizationId, versionId } = useAppRouteParams();
  const navigate = useNavigate();
  const location = useLocation();
  const updateVersion = useUpdateVersion();
  const getVersion = useGetVersion();
  const { showErrorNotification } = useAppNotificationManager();

  const { runWithToast } = useBulkActionToast({
    success: (count) => t.version.delete.success({ count }),
    error: (count) => t.version.delete.error({ count }),
  });

  const selectedVersions = useMemo(
    () => versions.filter(({ id }) => selectedVersionIds.includes(id)),
    [versions, selectedVersionIds]
  );

  const deletableVersions = useMemo(
    () => selectedVersions.filter((v) => !v.isDisabled),
    [selectedVersions]
  );

  const deletableCount = deletableVersions.length;

  const { renderDialogs } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const runDelete = async () => {
    const pathBeforeDelete = `${location.pathname}${location.search}`;

    setDialogPhase("in_progress");

    try {
      const { failedIds, successResults } = await runWithToast(
        deletableVersions,
        async (version) => {
          await updateVersion(
            {
              ...version,
              isDisabled: true,
            },
            {}
          );
        },
        {
          undoItem: async (_result, version) => {
            // Undo時は最新のバージョンをFirestoreから取得して他フィールドへの同時更新を保持
            const latestVersion = await getVersion(version.id, {
              organizationId,
            });
            if (!latestVersion) {
              throw new Error("バージョンが見つかりません");
            }
            await updateVersion(
              {
                ...latestVersion,
                isDisabled: false,
              },
              {}
            );
            if (version.id === versionId) {
              navigate(pathBeforeDelete);
            }
          },
        }
      );

      setSelectedVersionIds(failedIds);

      const successfullyDeletedIds = successResults.map(
        (result) => result.item.id
      );
      if (successfullyDeletedIds.includes(versionId)) {
        navigate(`/organizations/${organizationId}`);
      }
    } catch (error) {
      showErrorNotification(t.version.deleteDialog.error(), error);
    } finally {
      setDialogPhase(null);
    }
  };

  // 削除を実行すると Firestore の更新が先に反映されて versions から対象が消えるため、
  // 確認時点の対象を保持し、ダイアログを閉じ切るまで同じ内容を表示し続ける
  const confirmedTargetRef = useRef({
    displayMessage: t.version.deleteDialog.message({
      versionName: t.version.fallbackName(),
    }),
    additionalElement: undefined as React.ReactElement | undefined,
  });
  if (dialogPhase === "confirm") {
    const displayMessage =
      deletableCount === 1
        ? t.version.deleteDialog.message({
            versionName:
              deletableVersions[0]?.title || t.version.fallbackName(),
          })
        : t.version.deleteDialog.messageSome({ count: deletableCount });

    const additionalElement =
      isIncludingReferencedSheets === "including" ? (
        <div style={{ color: "red", marginTop: 8 }}>
          {t.version.deleteDialog.referencedSheetsWarning()}
        </div>
      ) : undefined;

    confirmedTargetRef.current = {
      displayMessage,
      additionalElement,
    };
  }
  const confirmedTarget = confirmedTargetRef.current;

  if (deletableCount === 0) {
    return null;
  }

  return (
    <>
      {renderDialogs({
        confirm: {
          title: t.version.deleteDialog.title(),
          displayMessage: confirmedTarget.displayMessage,
          onClick: runDelete,
          onClickCancel: () => {
            setSelectedVersionIds([]);
          },
          isThemeRed: true,
          buttonText: t.version.deleteDialog.button(),
          inProgressButtonText: t.version.deleteDialog.inProgressButton(),
          maxWidth: DIALOG_CONFIRM_MAX_WIDTH,
          element: confirmedTarget.additionalElement,
        },
      })}
    </>
  );
};
